import React from 'react';
import styled from 'styled-components';
import { screenReaderOnly } from '@peloton/accessibility';
import { gray1, white, superRedA11y, gray2, gray4, offBlack } from '@engage/colors';
import { spaces } from '@engage/styles';

import { label14Caps } from '@engage/typography';

const BOX_SIZE = `${spaces.large}px`;

const CheckboxUnselected: React.FC<React.PropsWithChildren<CheckboxIconProps>> = ({
  disabled,
  ...props
}) => (
  <UnselectedSvg aria-hidden={true} viewBox="0 0 21 20" {...props}>
    <path d="M17 3v16H1V3h16m1-1H0v18h18V2z" />
    <Path1 disabled={disabled} d="M1 3h16v16h-16z" />
  </UnselectedSvg>
);

const CheckboxSelected: React.FC<React.PropsWithChildren<CheckboxIconProps>> = ({
  disabled,
  ...props
}) => (
  <svg aria-hidden={true} viewBox="0 0 21 20" {...props}>
    <Path1 disabled={disabled} d="M1 3h16v16h-16z" />
    <Path2 disabled={disabled} d="M17,10h1V20H17ZM0,2H1V20H0ZM1,19H17v1H1ZM1,2H13V3H1Z" />
    <Path2 disabled={disabled} d="M10.3,16.4,2.9,9.1,5.1,6.9l4.8,4.9L18.2,0l2.4,1.7Z" />
  </svg>
);

const Unselected = styled(CheckboxUnselected)``;
const Selected = styled(CheckboxSelected)``;

export const Checkbox: React.FC<React.PropsWithChildren<Props>> = ({
  size = BOX_SIZE,
  onChange,
  testId,
  ...props
}: Props) => (
  <div>
    <StyledCheckbox
      type="checkbox"
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        onChange(event.currentTarget.checked)
      }
      {...props}
    />
    <div style={{ height: size, width: size }}>
      <Selected disabled={props.disabled} />
      <Unselected disabled={props.disabled} />
    </div>
  </div>
);

const UnselectedSvg = styled.svg`
  fill: ${gray4};
`;

const Path1 = styled.path`
  fill: ${(props: Disabled) => (props.disabled ? gray1 : white)};
`;

const Path2 = styled.path`
  fill: ${(props: Disabled) => (props.disabled ? gray2 : superRedA11y)};
`;

const StyledCheckbox = styled.input`
  ${screenReaderOnly}

  &[data-focus-method="keyboard"]:focus ~ div {
    /* Set outline for non-webkit browsers */
    outline: Highlight 2px solid;

    /* Sets outline to default for webkit browsers */
    @media (-webkit-min-device-pixel-ratio: 0) {
      outline: -webkit-focus-ring-color auto 5px;
    }
  }

  + div {
    ${Selected} {
      display: none;
    }

    ${Unselected} {
      display: block;
    }
  }

  :checked + div {
    ${Selected} {
      display: block;
    }

    ${Unselected} {
      display: none;
    }
  }
`;

type Disabled = {
  disabled?: boolean;
};

type IconProps = React.SVGProps<SVGSVGElement>;

type CheckboxIconProps = Pick<IconProps, Exclude<keyof IconProps, 'ref'>> & Disabled;

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

type Props = Pick<InputProps, Exclude<keyof InputProps, 'size' | 'onChange'>> & {
  label?: string;
  testId?: string;
  id: string;
  size?: string;
  onChange(checked: boolean): void;
};

const StyledRadio = styled.input`
  position: absolute;
  opacity: 0;

  & + div {
    ${label14Caps};
    color: ${offBlack};
    position: relative;
    padding-left: ${({ value }) => (!!value ? spaces.xxxLarge : spaces.large)}px;
    cursor: pointer;
    font-weight: 400;
    display: inline;

    &:before,
    &:after {
      content: '';
      position: absolute;
      border-radius: 100%;
      transition: all 0.1s ease;
    }

    &:before {
      width: 18px;
      height: 18px;
      left: 0;
      top: 0;
      background: ${white};
      border: 1px solid ${gray4};
    }

    &:after {
      width: 12px;
      height: 12px;
      background: ${superRedA11y};
      top: ${spaces.xxxxSmall}px;
      left: ${spaces.xxxxSmall}px;
      opacity: 0;
      transform: scale(0);
    }
  }

  &:checked {
    & + div {
      &:before {
        border-color: ${superRedA11y};
      }

      &:after {
        opacity: 1;
        transform: scale(1);
      }
    }
  }

  &[data-focus-method='keyboard']:focus ~ div {
    outline: Highlight 2px solid;

    @media (-webkit-min-device-pixel-ratio: 0) {
      outline: -webkit-focus-ring-color auto 5px;
    }
  }
`;

export const Radio: React.FC<
  React.PropsWithChildren<React.HTMLProps<HTMLInputElement>>
> = ({ ref, ...props }) => (
  <>
    <StyledRadio {...(props as any)} />
    <div />
  </>
);
